import React from "react";
import PageBreadcrumb from "@/components/common/PageBreadCrumb";
import PageMeta from "@/components/common/PageMeta";
import { useClients, CLIENTS_PER_PAGE, ClientsFilters } from '@/features/clients/hooks/useClients';
import { ClientsTable } from '@/features/clients/components/ClientsTable';
import { useSearchParams } from 'react-router-dom';
import Pagination from "@/components/common/Pagination";
import { TableColumnsFilter } from '@/components/ui/table/TableColumnsFilter';
import { Loader } from "@/components/ui/loader/Loader";
import { ClientFilters } from "@/features/clients/components/ClientFilters";
import { useTranslation } from 'react-i18next';

// Столбцы для таблицы клиентов, ключи должны соответствовать ключам в ClientsTable
const CLIENT_COLUMNS = [
  { key: 'client', header: 'clients.columns.client', field: 'client' },
  { key: 'verification', header: 'clients.columns.verification', field: 'verification_status' },
  { key: 'registered', header: 'clients.columns.registered', field: 'registered_at' },
  { key: 'location', header: 'clients.columns.location', field: 'country' },
  { key: 'actions', header: 'clients.columns.actions', field: 'actions' },
];

const CLIENTS_TABLE_STORAGE_KEY = 'clients-table-columns';
const DEFAULT_VISIBLE_COLUMNS = ['client', 'verification', 'registered', 'location', 'actions'];

const ClientsPage: React.FC = () => {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const currentPage = Number(searchParams.get('page')) || 1;

  // Получаем текущие фильтры из URL
  const currentFilters: ClientsFilters = {
    country: searchParams.get('country') || undefined,
    verification_status: (searchParams.get('verification_status') as 'new' | 'verified' | 'verification_needed' | undefined) || undefined,
    registration_start_date: searchParams.get('registration_start_date') || undefined,
    registration_end_date: searchParams.get('registration_end_date') || undefined,
    sort: {
      registration_date: searchParams.get('sort') as 'asc' | 'desc' || 'asc'
    }
  };

  const { data, isLoading } = useClients(currentPage, currentFilters);

  const handleFilterChange = (newFilters: ClientsFilters) => {
    const params = new URLSearchParams(searchParams);

    // Обновляем параметры фильтров
    Object.entries(newFilters).forEach(([key, value]) => {
      if (key === 'sort' && typeof value === 'object' && value !== null) {
        if (value.registration_date) {
          params.set('sort', value.registration_date);
        } else {
          params.delete('sort');
        }
      } else if (value) {
        params.set(key, value.toString());
      } else {
        params.delete(key);
      }
    });

    // Сбрасываем страницу при изменении фильтров
    params.set('page', '1');

    setSearchParams(params);
  };

  const handlePageChange = (page: number) => {
    const params = new URLSearchParams(searchParams);
    params.set('page', page.toString());
    setSearchParams(params);
  };

  return (
    <>
      <PageMeta
        title={t('clients.title')}
        description={t('clients.description')}
      />
      <PageBreadcrumb pageTitle={t('nav.clients')} />

      <div className="space-y-6">
        <TableColumnsFilter
          columns={CLIENT_COLUMNS}
          storageKey={CLIENTS_TABLE_STORAGE_KEY}
          defaultColumns={DEFAULT_VISIBLE_COLUMNS}
        >
          <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-4">
            <div className="flex flex-col md:flex-row gap-4 items-start md:items-center flex-1">
              <ClientFilters
                currentFilters={currentFilters}
                onFilterChange={handleFilterChange}
              />
            </div>
          </div>

          {isLoading ? (
            <div className="flex justify-center items-center min-h-[400px] bg-bg-secondary rounded-lg">
              <Loader />
            </div>
          ) : (
            <>
              <div className="bg-bg-secondary rounded-lg">
                <ClientsTable
                  clients={data?.data || []}
                  isLoading={false}
                />
              </div>
              <div className="flex justify-start md:justify-end mt-6">
                <Pagination
                  totalItems={data?.total || 0}
                  itemsPerPage={CLIENTS_PER_PAGE}
                  currentPage={currentPage}
                  onPageChange={handlePageChange}
                />
              </div>
            </>
          )}
        </TableColumnsFilter>
      </div>
    </>
  );
};

export default ClientsPage;
